import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, TrashIcon, EyeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import apiClient from '../utils/apiClient';
import { getDocumentUrl, shouldUseImageUrls } from '../utils/imageUtils';
import AttributeLabel from './AttributeLabel';
import ConfirmationModal from './ConfirmationModal';
import { ModalShell, ModalActions } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface Document {
  docId: number;
  filename: string;
  created_at: string;
}

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: Document[];
  category: 'insurance' | 'tollwut';
  categoryId: number;
  dogId: number;
  dogName: string;
  onDocumentsChange: () => void;
  onDocumentCountChange?: (newCount: number) => void;
}

interface ConfirmModalState {
  isOpen: boolean;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant?: 'single' | 'two';
  onConfirm?: () => void;
}

// PDF.js Worker-Setup
const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  documents,
  category,
  categoryId,
  dogId,
  dogName,
  onDocumentsChange,
  onDocumentCountChange
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({ isOpen: false, title: '', message: '' });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [localDocuments, setLocalDocuments] = useState<Document[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // PDF.js Worker konfigurieren
  useEffect(() => {
    GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
  }, []);


  // Helper function to check if file is PDF
  const isPdfFile = (filename: string) => {
    return filename.toLowerCase().includes('.pdf');
  };

  // DEBUG: Log beim Öffnen des Modals - NUR wenn Modal neu geöffnet wird
  useEffect(() => {
    if (isOpen && localDocuments.length === 0) {
      // Nur beim ersten Öffnen oder wenn keine lokalen Dokumente vorhanden
      console.log('=== MODAL ERSTMALIG GEÖFFNET ===');
      console.log(`Kategorie: ${category}`);
      console.log(`Hund: ${dogName} (ID: ${dogId})`);
      console.log('Initial Documents:', documents);

      // Filtere Placeholder-Dokumente raus (temporäre IDs oder "uploading-" Dateinamen)
      const realDocuments = documents.filter(doc =>
        doc.docId < 1000000000000 && // Echte IDs sind kleiner
        !doc.filename.includes('uploading-') &&
        !doc.filename.includes('placeholder-')
      );

      console.log('Gefilterte echte Dokumente:', realDocuments);
      setLocalDocuments([...realDocuments]);
      console.log('LocalDocuments gesetzt:', [...realDocuments]);
    } else if (isOpen) {
      console.log('=== MODAL WIEDER GEÖFFNET ===');
      console.log('Behalte bestehende LocalDocuments:', localDocuments);
      console.log('Ignoriere Parent Documents:', documents);
    }
  }, [isOpen]);

  // Reset currentIndex beim Öffnen
  useEffect(() => {
    if (isOpen) {
      console.log(`Setze currentIndex auf 0, Dokumente vorhanden: ${localDocuments.length}`);
      setCurrentIndex(0);
    }
  }, [isOpen]);

  // Dokument laden basierend auf currentIndex und localDocuments - NUR wenn Modal offen ist
  useEffect(() => {
    // NUR ausführen wenn Modal offen ist
    if (!isOpen) {
      console.log('-> Modal geschlossen, überspringe Dokumentenladen');
      return;
    }

    console.log(`=== DOKUMENT LADEN ===`);
    console.log(`CurrentIndex: ${currentIndex}`);
    console.log('LocalDocuments:', localDocuments);

    if (localDocuments.length > 0 && localDocuments[currentIndex]) {
      const currentDoc = localDocuments[currentIndex];
      console.log(`Lade Dokument:`, currentDoc);

      // Starte Übergangsanimation
      setIsTransitioning(true);

      if (isPdfFile(currentDoc.filename)) {
        console.log(`-> PDF wird geladen (ID: ${currentDoc.docId})`);
        loadPdfDocument(currentDoc.docId);
        // Reset image state
        setImageUrl(null);
        setImageError(null);
        setLoadingImage(false);
      } else if (isImageFile(currentDoc.filename)) {
        console.log(`-> Bild wird geladen (ID: ${currentDoc.docId})`);
        loadImageDocument(currentDoc.docId);
        // Reset PDF state
        setDocumentUrl(null);
        setPdfError(null);
        setLoadingPdf(false);
      } else {
        console.log(`-> Unsupported file type: ${currentDoc.filename}`);
        // Reset both states for unsupported files
        setDocumentUrl(null);
        setPdfError(null);
        setLoadingPdf(false);
        setImageUrl(null);
        setImageError(null);
        setLoadingImage(false);
        setIsTransitioning(false);
      }
    } else if (localDocuments.length === 0) {
      console.log('-> Keine Dokumente vorhanden');
      // Reset all states only if no documents
      setDocumentUrl(null);
      setPdfError(null);
      setLoadingPdf(false);
      setImageUrl(null);
      setImageError(null);
      setLoadingImage(false);
      setIsTransitioning(false);
    } else {
      console.log(`-> Index ${currentIndex} außerhalb des Bereichs (${localDocuments.length} Dokumente)`);
      // Reset all states
      setDocumentUrl(null);
      setPdfError(null);
      setLoadingPdf(false);
      setImageUrl(null);
      setImageError(null);
      setLoadingImage(false);
      setIsTransitioning(false);
    }
  }, [isOpen, currentIndex, localDocuments]);

  const loadImageDocument = async (docId: number) => {
    try {
      setLoadingImage(true);
      setImageError(null);

      // Cleanup old blob URL
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }

      const response = await apiClient.get(`/api/members/documents/download/${docId}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'image/*'
        }
      });

      const blob = response.data;

      // Überprüfe ob die Antwort wirklich ein Bild ist
      if (blob.size === 0) {
        throw new Error('Die Datei ist leer');
      }

      const mimeType = response.headers['content-type'] || 'image/jpeg';

      // Versuche festzustellen, ob es wirklich ein Bild ist
      if (!mimeType.includes('image') && !mimeType.includes('application/octet-stream')) {
        throw new Error(`Dateityp nicht unterstützt: ${mimeType}`);
      }

      const blobUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
      setImageUrl(blobUrl);
      setIsTransitioning(false);
    } catch (err: any) {
      // console.error('Error loading image document:', err);
      let errorMsg = 'Unbekannter Fehler';

      if (err.response?.status === 404) {
        errorMsg = 'Dokument nicht gefunden';
      } else if (err.response?.status === 403) {
        errorMsg = 'Keine Berechtigung zum Zugriff auf das Dokument';
      } else if (err.response?.status >= 500) {
        errorMsg = 'Serverfehler beim Laden des Dokuments';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.response?.status) {
        errorMsg = `HTTP-Fehler ${err.response.status}`;
      }

      setImageError(`Bild-Vorschau konnte nicht geladen werden: ${errorMsg}`);
      setIsTransitioning(false);
    } finally {
      setLoadingImage(false);
    }
  };

  const loadPdfDocument = async (docId: number) => {
    try {
      setLoadingPdf(true);
      setPdfError(null);

      // Cleanup old blob URL
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }

      const response = await apiClient.get(`/api/members/documents/download/${docId}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf, image/*'
        }
      });

      const blob = response.data;

      // Überprüfe ob die Antwort wirklich ein PDF ist
      if (blob.size === 0) {
        throw new Error('Die Datei ist leer');
      }

      const mimeType = response.headers['content-type'] || 'application/pdf';

      // Versuche festzustellen, ob es wirklich ein PDF ist
      if (!mimeType.includes('pdf') && !mimeType.includes('application/octet-stream')) {
        throw new Error(`Dateityp nicht unterstützt: ${mimeType}`);
      }

      const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      setDocumentUrl(blobUrl);
      setIsTransitioning(false);
    } catch (err: any) {
      // console.error('Error loading PDF document:', err);
      let errorMsg = 'Unbekannter Fehler';

      if (err.response?.status === 404) {
        errorMsg = 'Dokument nicht gefunden';
      } else if (err.response?.status === 403) {
        errorMsg = 'Keine Berechtigung zum Zugriff auf das Dokument';
      } else if (err.response?.status >= 500) {
        errorMsg = 'Serverfehler beim Laden des Dokuments';
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.response?.status) {
        errorMsg = `HTTP-Fehler ${err.response.status}`;
      }

      setPdfError(`PDF-Vorschau konnte nicht geladen werden: ${errorMsg}`);
      setIsTransitioning(false);
    } finally {
      setLoadingPdf(false);
    }
  };

  // Cleanup document URLs when component unmounts
  useEffect(() => {
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [documentUrl, imageUrl]);


  // Reset localDocuments beim Schließen
  useEffect(() => {
    if (!isOpen && localDocuments.length > 0) {
      console.log('=== MODAL GESCHLOSSEN - RESET ===');
      // Reset alle States
      setLocalDocuments([]);
      setCurrentIndex(0);
      setDocumentUrl(null);
      setPdfError(null);
      setLoadingPdf(false);
      setImageUrl(null);
      setImageError(null);
      setLoadingImage(false);
      setIsTransitioning(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentDocument = localDocuments[currentIndex];
  const categoryName = category === 'insurance' ? 'Haftpflichtversicherung' : 'Tollwutimpfung';

  const handleModalClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  const handlePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : localDocuments.length - 1;
    console.log(`=== BLÄTTERN RÜCKWÄRTS ===`);
    console.log(`Von Index ${currentIndex} zu ${newIndex}`);
    console.log('Aktuelle Dokumente:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < localDocuments.length - 1 ? currentIndex + 1 : 0;
    console.log(`=== BLÄTTERN VORWÄRTS ===`);
    console.log(`Von Index ${currentIndex} zu ${newIndex}`);
    console.log('Aktuelle Dokumente:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
    setCurrentIndex(newIndex);
  };

  const handleDeleteDocument = async (docId: number) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Dokument löschen',
      message: `Möchten Sie dieses ${categoryName}-Dokument wirklich löschen?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          setIsDeleting(true);
          const response = await apiClient.delete(`/api/dogs/delete_dog_document/${dogId}/${docId}`);

          if (response.data.success) {
            console.log(`=== DOKUMENT LÖSCHEN ===`);
            console.log(`Gelöschtes Dokument ID: ${docId}`);
            console.log('Dokumente VORHER:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));

            // STOPPE alle laufenden Ladeprozesse SOFORT
            setLoadingPdf(false);
            setLoadingImage(false);
            setIsTransitioning(false);

            // Reset alle States vor der Dokumentenaktualisierung
            setDocumentUrl(null);
            setPdfError(null);
            setImageUrl(null);
            setImageError(null);

            // Entferne das Dokument aus lokalDocuments
            const updatedDocuments = localDocuments.filter(doc => doc.docId !== docId);
            console.log('Dokumente NACHHER:', updatedDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
            console.log(`Dokumentenanzahl: ${localDocuments.length} -> ${updatedDocuments.length}`);

            // Informiere SOFORT über neue Dokumentenanzahl (BEVOR State-Updates)
            if (onDocumentCountChange) {
              console.log(`-> onDocumentCountChange aufgerufen mit: ${updatedDocuments.length}`);
              onDocumentCountChange(updatedDocuments.length);
            }

            // Aktualisiere lokale Dokumente
            setLocalDocuments(updatedDocuments);

            // Intelligente Navigation nach dem Löschen
            if (updatedDocuments.length === 0) {
              console.log('-> Letztes Dokument gelöscht, setze Index auf 0');
              setCurrentIndex(0);
            } else {
              let newIndex = currentIndex;

              // Wenn wir das letzte Dokument gelöscht haben, gehe zum vorigen
              if (currentIndex >= updatedDocuments.length) {
                newIndex = updatedDocuments.length - 1;
                console.log(`-> Index angepasst: ${currentIndex} -> ${newIndex} (letztes Dokument gelöscht)`);
              } else {
                console.log(`-> Index bleibt bei ${currentIndex} (nächstes Dokument wird angezeigt)`);
              }

              setCurrentIndex(newIndex);
            }

            // Erfolgs-Modal
            setConfirmModal({
              isOpen: true,
              type: 'success',
              title: 'Erfolgreich',
              message: 'Dokument wurde erfolgreich gelöscht.',
              variant: 'single'
            });

            console.log('=== LÖSCHEN ABGESCHLOSSEN ===');
            // Rufe onDocumentsChange auf, um echte Daten vom Server zu holen
            if (onDocumentsChange) {
              onDocumentsChange();
            }
          } else {
            throw new Error('Fehler beim Löschen');
          }
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: 'Das Dokument konnte nicht gelöscht werden.',
            variant: 'single'
          });
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('document', file);
      formData.append('category_id', categoryId.toString());
      formData.append('description', `${categoryName} - ${file.name}`);

      const response = await apiClient.post(`/api/dogs/upload_dog_document/${dogId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        console.log(`=== DOKUMENT HOCHLADEN ===`);
        console.log(`Hochgeladene Datei: ${file.name}`);
        console.log('Dokumente VORHER:', localDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));

        // Erstelle ein neues Dokument-Objekt (mit der ID aus der API-Antwort)
        const newDocument: Document = {
          docId: response.data.id,
          filename: `${categoryId === 1 ? 'tollwut' : 'insurance'}/${file.name}`,
          created_at: new Date().toISOString()
        };

        // Füge das neue Dokument zu localDocuments hinzu
        const updatedDocuments = [...localDocuments, newDocument];
        console.log('Neues Dokument:', newDocument);
        console.log('Dokumente NACHHER:', updatedDocuments.map(doc => ({id: doc.docId, filename: doc.filename})));
        console.log(`Dokumentenanzahl: ${localDocuments.length} -> ${updatedDocuments.length}`);

        // Aktualisiere lokale Dokumente
        setLocalDocuments(updatedDocuments);

        // Informiere über neue Dokumentenanzahl SOFORT
        if (onDocumentCountChange) {
          console.log(`-> onDocumentCountChange aufgerufen mit: ${updatedDocuments.length}`);
          onDocumentCountChange(updatedDocuments.length);
        }

        // Springe zum neu hochgeladenen Dokument
        const newDocumentIndex = updatedDocuments.length - 1;
        console.log(`-> Springe zu neuem Dokument (Index: ${newDocumentIndex})`);
        setCurrentIndex(newDocumentIndex);

        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Dokument wurde erfolgreich hochgeladen.',
          variant: 'single'
        });

        console.log('=== HOCHLADEN ABGESCHLOSSEN ===');
        // Rufe onDocumentsChange auf, um echte Daten vom Server zu holen
        if (onDocumentsChange) {
          onDocumentsChange();
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Das Dokument konnte nicht hochgeladen werden.',
        variant: 'single'
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const isImageFile = (filename: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().includes(ext));
  };

  return (
    <>
      <ModalShell
        open={isOpen}
        onClose={handleModalClose}
        variant="viewer"
        size="lg"
        mobilePresentation="fullscreen"
        icon={EyeIcon}
        title={`${categoryName} - ${dogName}`}
        subtitle={localDocuments.length > 0 ? `${currentIndex + 1} von ${localDocuments.length} Dokumenten` : 'Keine Dokumente'}
        footer={
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center gap-2.5">
              {currentDocument && (
                <button
                  onClick={() => handleDeleteDocument(currentDocument.docId)}
                  disabled={isDeleting}
                  aria-label="Dokument löschen"
                  title="Dokument löschen"
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-error-500/10 text-error-400 ring-1 ring-inset ring-error-500/25 transition-colors hover:bg-error-500/20 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-error-400 border-t-transparent" />
                  ) : (
                    <TrashIcon className="h-4 w-4" />
                  )}
                </button>
              )}
              <div className="min-w-0 flex-1">
                <ModalActions
                  primary={{
                    label: isUploading ? 'Hochladen...' : 'Dokument hinzufügen',
                    onClick: () => fileInputRef.current?.click(),
                    loading: isUploading,
                    icon: <PlusIcon className="h-5 w-5" />,
                  }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-400">PDF, JPG, PNG, GIF (max. 10MB)</p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.gif"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
          </div>
        }
      >
        <div className="flex h-full min-h-[60vh] flex-col sm:min-h-[70vh]">
          {localDocuments.length > 0 && currentDocument ? (
            <>
              {/* Navigation */}
              {localDocuments.length > 1 && (
                <div className="flex items-center justify-between gap-2 border-b border-gray-800 bg-gray-900 px-4 py-3 sm:px-6">
                  <button
                    onClick={handlePrevious}
                    className="flex items-center gap-2 rounded-lg bg-gray-800 px-3 py-2 text-gray-200 transition-colors hover:bg-gray-700"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                    Vorheriges
                  </button>

                  <span className="inline-flex items-center gap-1 text-sm text-gray-400">
                    <AttributeLabel iconKey="date">Hochgeladen:</AttributeLabel>
                    {new Date(currentDocument.created_at).toLocaleDateString('de-DE')}
                  </span>

                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 rounded-lg bg-gray-800 px-3 py-2 text-gray-200 transition-colors hover:bg-gray-700"
                  >
                    Nächstes
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              )}

              {/* Document Content */}
              <div className="relative flex flex-1 items-center justify-center overflow-auto bg-gray-900 p-4">
                {/* Loading Overlay */}
                {isTransitioning && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-900">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500"></div>
                      <span className="text-gray-400">Dokument wird geladen...</span>
                    </div>
                  </div>
                )}

                {isImageFile(currentDocument.filename) ? (
                  <>
                    {loadingImage ? (
                      <div className="flex items-center justify-center">
                        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-500"></div>
                        <p className="ml-3 text-gray-400">Bild wird geladen...</p>
                      </div>
                    ) : imageError ? (
                      <div className="text-center">
                        <div className="mb-4">
                          <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-gray-500" />
                        </div>
                        <h3 className="mb-2 text-lg font-medium text-gray-100">
                          Bild nicht verfügbar
                        </h3>
                        <p className="mb-4 text-gray-400">
                          {imageError}
                        </p>
                        <p className="text-sm text-gray-500">
                          Datei: {currentDocument.filename.split('/').pop()}
                        </p>
                      </div>
                    ) : imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="Dokument"
                        className="max-h-full max-w-full rounded-lg object-contain shadow-sm"
                      />
                    ) : (
                      <div className="text-center">
                        <div className="mb-4">
                          <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-gray-500" />
                        </div>
                        <h3 className="mb-2 text-lg font-medium text-gray-100">
                          Bild nicht verfügbar
                        </h3>
                        <p className="mb-4 text-gray-400">
                          Das Bild konnte nicht geladen werden.
                        </p>
                        <p className="text-sm text-gray-500">
                          Datei: {currentDocument.filename.split('/').pop()}
                        </p>
                      </div>
                    )}
                  </>
                ) : isPdfFile(currentDocument.filename) ? (
                  <div className="flex h-full w-full flex-col">
                    {pdfError ? (
                      <div className="flex flex-1 items-center justify-center text-center">
                        <div>
                          <ExclamationTriangleIcon className="mx-auto mb-4 h-16 w-16 text-gray-500" />
                          <h3 className="mb-2 text-lg font-medium text-gray-100">
                            PDF nicht verfügbar
                          </h3>
                          <p className="mb-4 text-gray-400">
                            {pdfError}
                          </p>
                        </div>
                      </div>
                    ) : loadingPdf ? (
                      <div className="flex flex-1 items-center justify-center">
                        <div className="text-center">
                          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary-500"></div>
                          <p className="text-gray-400">PDF wird geladen...</p>
                        </div>
                      </div>
                    ) : documentUrl ? (
                      <div className="h-full w-full bg-gray-800">
                        <Worker workerUrl={PDFJS_WORKER_URL}>
                          <div
                            className="w-full overflow-auto"
                            style={{
                              height: '60vh',
                              minHeight: '500px',
                              maxHeight: '75vh',
                              border: '1px solid #374151'
                            }}
                          >
                            <Viewer
                              fileUrl={documentUrl}
                              defaultScale={SpecialZoomLevel.PageWidth}
                            />
                          </div>
                        </Worker>
                      </div>
                    ) : (
                      <div className="flex flex-1 items-center justify-center text-center">
                        <div>
                          <EyeIcon className="mx-auto mb-4 h-16 w-16 text-gray-500" />
                          <p className="mb-4 text-gray-400">
                            PDF wird vorbereitet...
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="mb-4">
                      <EyeIcon className="mx-auto h-16 w-16 text-gray-500" />
                    </div>
                    <p className="mb-4 text-gray-400">
                      Dokument-Vorschau nicht verfügbar
                    </p>
                    <a
                      href={shouldUseImageUrls() ? getDocumentUrl(currentDocument.docId) : ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
                    >
                      <EyeIcon className="h-5 w-5" />
                      Dokument öffnen
                    </a>
                  </div>
                )}
              </div>

              {/* Document Info */}
              <div className="border-t border-gray-800 bg-gray-900 px-4 py-3 sm:px-6">
                <p className="text-sm font-medium text-gray-100">
                  {currentDocument.filename.split('/').pop()}
                </p>
                <p className="inline-flex items-center gap-1 text-xs text-gray-400">
                  <AttributeLabel iconKey="date" size="sm">Hochgeladen:</AttributeLabel>
                  {new Date(currentDocument.created_at).toLocaleDateString('de-DE')}
                </p>
              </div>
            </>
          ) : (
            /* No Documents */
            <div className="flex flex-1 items-center justify-center p-8">
              <div className="text-center">
                <EyeIcon className="mx-auto mb-4 h-16 w-16 text-gray-600" />
                <h3 className="mb-2 text-lg font-medium text-gray-100">
                  Keine Dokumente vorhanden
                </h3>
                <p className="mb-6 text-gray-400">
                  Es wurden noch keine {categoryName}-Dokumente für {dogName} hochgeladen.
                </p>
              </div>
            </div>
          )}
        </div>
      </ModalShell>

      {/* Info-/Bestätigungsdialog (Löschen, Erfolg, Fehler) */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        button={
          confirmModal.variant !== 'two'
            ? { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) }
            : undefined
        }
        buttons={
          confirmModal.variant === 'two'
            ? {
                cancel: { text: 'Abbrechen', onPress: () => setConfirmModal(prev => ({ ...prev, isOpen: false })) },
                confirm: {
                  text: 'Bestätigen',
                  onPress: () => {
                    confirmModal.onConfirm?.();
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                  },
                },
              }
            : undefined
        }
      />
    </>
  );
};

export default DocumentPreviewModal;
